import { FormikValues, getIn } from 'formik';

export const capitalize = (word: string) => {
  if (word) {
    const firstChar = word.charAt(0).toUpperCase();
    return firstChar + word.slice(1).toLocaleLowerCase();
  }
  return '';
};

export const capitalizeEachWord = (words: string | undefined) => {
  if (words) {
    const output = words.split(' ').map((word) => capitalize(word));
    return output.join(' ');
  }
  return '';
};

/**
 * Removes leading zeros from 16,9, or 8 character strings
 * Example: "035507601" to "35507601"
 */
export const removeLeadingZeros = (input: string) => {
  const regex = /\b(?:0*([1-9][0-9]{7}|[1-9][0-9]{8}|[1-9][0-9]{15}))?\b/;
  const match = input.match(regex);
  if (match && match[1]) {
    return input.replace(match[0], match[1]);
  }
  return input;
};

/**
 * Removes and any spaces in a str
 * Example: " this contains       spaces" to "this contains spaces"
 */
export const removeExtraWhiteSpace = (str: string): string => {
  return str.replace(/\s+/g, ' ').trim();
};

export const removeAllWhiteSpace = (str: string): string => {
  return str.replace(/\s/g, '');
};

/**
 * Formats a phone number like "(999) 999-9999" which should be the
 * standard across the site.
 */
export const formatPhoneNumber = (value: string): string => {
  // if input value is falsy eg if the user deletes the input, then just return
  if (!value) {
    return value;
  }

  // clean the input for any non-digit values.
  const phoneNumber = value.replace(/[^\d]/g, '');

  // phoneNumberLength is used to know when to apply our formatting for the phone number
  const phoneNumberLength = phoneNumber.length;

  // we need to return the value with no formatting if its less then four digits
  // this is to avoid weird behavior that occurs if you  format the area code to early

  if (phoneNumberLength < 4) {
    return phoneNumber;
  }

  // if phoneNumberLength is greater than 4 and less the 7 we start to return
  // the formatted number
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }

  // finally, if the phoneNumberLength is greater then seven, we add the last
  // bit of formatting and return it.
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
};

/**
 * Clean up the phone number to remove non-digit values ex: "1234567890"
 * standard across the site.
 */
export const cleanUpPhoneNumber = (value: string): string => {
  // if input value is falsy eg if the user deletes the input, then just return
  if (!value) {
    return value;
  }

  // clean the input for any non-digit values.
  return value.replace(/[^\d]/g, '');
};

export const formatSecsAsTime = (seconds: number): string => {
  return new Date(seconds * 1000).toISOString().slice(11, 19);
};

/**
 * separate words that begin with a capital letter.
 * For example, 'JohnDoe' to 'John Doe'.
 * @param val string to separate
 */
export const separateWords = (val: string | null | undefined): string =>
  val ? val.split(/(?=[A-Z])/).join(' ') : '';

/**
 * General RegEx for email pattern
 */
export const emailPattern =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const isValidEmail = (email: string): boolean => {
  return emailPattern.test(email.replace(/\s/g, '').toLowerCase());
};

/**
 * Mask for phone numbers
 */
export const maskedPhoneNumber = (phone: string): string => {
  if (!phone) {
    return '';
  }

  const cleaned = `${phone}`.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }
  return phone.replace(/-/g, '');
};

export const phoneRegex =
  /^\(?([2-9]{1})\)?([0-9]{2})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;

/**
 * General phone validation
 */
export const validatePhoneNumber = (phone: string): boolean => {
  const match = phoneRegex;
  return !!(!!phone && phone.match(match));
};

export const parseInitials = (name: string) => {
  const [firstName, lastName] = (name || '').split(' ');
  if (!lastName) {
    return firstName[0];
  }
  return firstName[0] + lastName[0];
};

/**
 * Remove dashes from the phone number
 */
export const cleanPhoneNumber = (phone: string): string => {
  return phone.replace(/-/g, '');
};

export const formatNumberWithCommas = (number: number): string => {
  return number.toLocaleString('en-US');
};

export const toBase64 = (value: string): string => window.btoa(value);
export const fromBase64 = (value: string): string => window.atob(value);

export const getClientInfo = () => {
  const { navigator } = window;
  const windowUserAgent = window.navigator.userAgent;

  // NOTE: You can use these valid user agents to debug this code if it has issues.
  // const testCases = [
  //   'Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko',
  //   'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/103.0.0.0 Safari/537.36 OPR/88.0.4412.40',
  //   'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/42.0.2311.135 Safari/537.36 Edge/12.246',
  //   'Mozilla/5.0 (X11; Ubuntu; Linux x86_64; rv:15.0) Gecko/20100101 Firefox/15.0.1',
  //   'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_11_2) AppleWebKit/601.3.9 (KHTML, like Gecko) Version/9.0.2 Safari/601.3.9',
  //   'Mozilla/5.0 (iPhone14,3; U; CPU iPhone OS 15_0 like Mac OS X) AppleWebKit/602.1.50 (KHTML, like Gecko) Version/10.0 Mobile/19A346 Safari/602.1',
  //   'Mozilla/5.0 (Linux; Android 10; SM-G996U Build/QP1A.190711.020; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Mobile Safari/537.36',
  //   'Mozilla/5.0 (Linux; Android 9; SM-G973U Build/PPR1.180610.011) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/69.0.3497.100 Mobile Safari/537.36',
  //   'Mozilla/5.0 (Windows Phone 10.0; Android 6.0.1; Microsoft; RM-1152) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/52.0.2743.116 Mobile Safari/537.36 Edge/15.15254',
  //   'Mozilla/5.0(iPad; U; CPU iPhone OS 3_2 like Mac OS X; en-us) AppleWebKit/531.21.10 (KHTML, like Gecko) Version/4.0.4 Mobile/7B314 Safari/531.21.10',
  // ];

  const getPlatformInfoFromUserAgent = (userAgent: string) => {
    const desktopPlatformMatches =
      userAgent.match(/\b\w*(macintosh|windows|linux)/i) || [];
    const mobilePlatformMathces =
      userAgent.match(/\b\w*(iphone|ipad|windows phone|android)/i) || [];
    const isMobile = mobilePlatformMathces.length !== 0;

    const platformName =
      (isMobile ? mobilePlatformMathces[0] : desktopPlatformMatches[0]) ||
      'unrecognized';

    return {
      name: platformName,
      isMobile: isMobile || null,
    };
  };

  const getBrowserInfo = (userAgent: string) => {
    let tem;
    let browserString = '';
    const browserMatches =
      userAgent.match(
        /(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i
      ) || [];

    let browserInfo = browserMatches[2]
      ? [browserMatches[1], browserMatches[2]]
      : [navigator.appName, navigator.appVersion, '-?'];

    if (/\b\w*(trident)/i.test(browserMatches[1])) {
      tem = /\brv[ :]+(\d+)/g.exec(userAgent) || [];
      browserInfo = ['IE', tem[1]];
      browserString = `IE ${tem[1] || ''}`;
    }

    if (browserMatches[1] === 'Chrome') {
      tem = userAgent.match(/\b(OPR|Edge)\/(\d+)/);
      browserInfo = [browserMatches[1], browserMatches[2]];
      if (tem != null) {
        browserString = tem[1].replace('OPR', 'Opera');
        browserInfo = [browserString, tem[2]];
      }
    }

    return {
      name: browserInfo[0] || 'unrecognized',
      version: browserInfo[1] || null,
    };
  };

  const platformInfo = getPlatformInfoFromUserAgent(windowUserAgent);

  const browserInfo = getBrowserInfo(windowUserAgent);

  return {
    userAgent: windowUserAgent,
    os: platformInfo.name,
    isMobile: platformInfo.isMobile,
    browser: browserInfo.name,
    browserVersion: browserInfo.version,
  };
};

export const touchedErrorCheck = (
  formikProps: FormikValues,
  targetKey: string
) => {
  return getIn(formikProps.errors, `[${targetKey}]`) &&
    getIn(formikProps.touched, `[${targetKey}]`)
    ? getIn(formikProps.errors, `[${targetKey}]`)
    : '';
};

/**
 *
 * @param index
 * @param keyName
 * @param targetKey
 * @param formikProps
 * @returns String of an error message, must be setup in formikValidatorfile
 *
 *  errorMessage={
 *    childArrayTouchedErrorCheck(
 *       formikProps,
 *       'rules',
 *       index,
 *       'name'
 *     )}
 */
export const childArrayTouchedErrorCheck = (
  formikProps: FormikValues,
  arrayKeyName: string,
  index: number,
  targetKey: string
) => {
  return getIn(
    formikProps.errors,
    `[${arrayKeyName}][${index}][${targetKey}]`
  ) && getIn(formikProps.touched, `[${arrayKeyName}][${index}][${targetKey}]`)
    ? getIn(formikProps.errors, `[${arrayKeyName}][${index}][${targetKey}]`)
    : '';
};

export const scrollToTopOfPage = (topFormRef: React.RefObject<HTMLElement>) =>
  topFormRef.current!.scrollIntoView({
    behavior: 'smooth',
  });

export const formatDateParam = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(date.getDate()).padStart(2, '0')}`;
